import _ from 'lodash'
import {DateTime} from 'luxon'
import {createContext, FC, useContext, useEffect, useMemo, useState} from 'react'

import {BrandFragment} from '@/utils/graphqlSdk'
import {
  sortBrands,
  useAreItemsUnassigned,
  useBrands,
  useInvalidateBrandQueries,
} from '@/utils/user-portal/dataFetchers/brand'

export type SelectedBrand = BrandFragment | (Omit<BrandFragment, 'id' | 'deleted'> & {id: null})

type BrandContextType = {
  setSelectedBrandId: (brandId: string | null) => void
  selectedBrand: SelectedBrand
  brands: BrandFragment[]
  isLoading: boolean
  isError: boolean
  isFetched: boolean
  areItemsUnassigned: boolean
  invalidateBrandQueries: () => void
  refetch: () => void
}

const UnassignedBrand = {
  id: null,
  name: 'Unassigned',
  user_id: '',
  created_at: '',
  created_by_user_id: null,
  updated_by_user_id: null,
  updated_at: '',
  websites: [],
}

const BrandContext = createContext<BrandContextType>({
  setSelectedBrandId: _.identity,
  selectedBrand: UnassignedBrand,
  brands: [],
  isLoading: true,
  isError: false,
  isFetched: false,
  areItemsUnassigned: false,
  invalidateBrandQueries: _.identity,
  refetch: _.identity,
})

const SELECT_BRAND_LOCAL_STORAGE_KEY = 'selectedBrand'

const parseSelectedBrand = (): string | undefined => {
  if (typeof localStorage !== 'undefined') {
    const selectedBrand = localStorage.getItem(SELECT_BRAND_LOCAL_STORAGE_KEY)

    return JSON.parse(selectedBrand ?? '{}').brandId ?? undefined
  }

  return undefined
}

export const useBrandContext = (): BrandContextType => useContext(BrandContext)

export const BrandProvider: FC<React.PropsWithChildren<unknown>> = ({children}) => {
  const [_selectedBrandId, _setSelectedBrandId] = useState<string | null | undefined>(() =>
    parseSelectedBrand(),
  )

  const setSelectedBrandId = (brandId: string | null) => {
    _setSelectedBrandId(brandId)

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        SELECT_BRAND_LOCAL_STORAGE_KEY,
        JSON.stringify({
          brandId,
          updated_at: DateTime.now(),
        }),
      )
    }
  }

  const invalidateBrandQueries = useInvalidateBrandQueries()

  const {data, isLoading, isFetched, isError, refetch} = useBrands()

  const {
    data: areItemsUnassigned = false,
    isLoading: isLoadingAggregate,
    isFetched: isFetchedAggregate,
    isError: isErrorAggregate,
    refetch: refetchAggregate,
  } = useAreItemsUnassigned()

  useEffect(() => {
    if (!areItemsUnassigned && _selectedBrandId === null) {
      _setSelectedBrandId(undefined)
    }
  }, [areItemsUnassigned, _selectedBrandId])

  const sortedBrands = useMemo(() => sortBrands(data), [data])

  const refetchAll = async () => {
    await Promise.all([refetch(), refetchAggregate()])
  }

  return (
    <BrandContext.Provider
      value={{
        setSelectedBrandId,
        selectedBrand:
          _selectedBrandId === null
            ? UnassignedBrand
            : data?.find((brand) => brand.id === _selectedBrandId) ?? data?.[0] ?? UnassignedBrand,
        isLoading: isLoading || isLoadingAggregate,
        isError: isError || isErrorAggregate,
        isFetched: isFetched || isFetchedAggregate,
        refetch: refetchAll,
        brands: sortedBrands,
        areItemsUnassigned,
        invalidateBrandQueries,
      }}
    >
      {children}
    </BrandContext.Provider>
  )
}
